import { LinkButton } from 'components/controls/LinkButton';
import { IVirtualTableColumn } from 'components/layout/VirtualTable/Table/components/VirtualTable';
import { getConcernLevelColor } from 'shared/utils/concern-level.utils';
import { NearMiss } from '../../../queries/near-miss-list.queries.types';
import CellContainer from 'components/Shared/CellContainer';
import i18next from 'i18next';
import { DateTime } from 'luxon';

export const getNearMissListTableColumns = (
   handleColumnClick: (ev: React.MouseEvent<HTMLElement>, column: IVirtualTableColumn<NearMiss>) => void,
   handleRowClick: (item: NearMiss) => void
): IVirtualTableColumn<NearMiss>[] => {

   const formatDate = (date?: Date): string => {
      if (!date) return '';

      const dt = DateTime.fromJSDate(date).setLocale(i18next.language);
      return dt.toLocaleString({...DateTime.DATE_SHORT, weekday: 'short', month: 'short'});
   };

   return [
      {
         key: 'column1',
         name: 'ObservationList.CaseId',
         fieldName: 'observationId',
         minWidth: 40,
         maxWidth: i18next.language == 'gb' ? 30 : 60,
         isResizable: false,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => <CellContainer>{item.observationId}</CellContainer>
      },
      {
         key: 'column2',
         name: 'ObservationList.IncidentDate',
         fieldName: 'incidentDate',
         minWidth: 110,
         maxWidth: 115,
         isResizable: false,
         isSorted: true,
         isSortedDescending: true,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => (
            <CellContainer>
               {formatDate(new Date(item.incidentDate))}
            </CellContainer>
         )
      },
      {
         key: 'column3',
         name: 'ObservationList.ConcernLevel',
         fieldName: 'concernLevel',
         minWidth: 80,
         maxWidth: i18next.language == 'gb' ? 90 : 145,
         isResizable: false,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted Low to High',
         sortDescendingAriaLabel: 'Sorted High to Low',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => (
            <CellContainer style={{ color: getConcernLevelColor(item.concernLevel) }}>
               {i18next.t(`ObservationFilter.${item.concernLevel.toProperCase()}`)}
            </CellContainer>
         ),
         sortValueResolver: (item: NearMiss) => {
            i18next.t(`ObservationFilter.${item.concernLevel.toProperCase()}`);
         }
      },
      {
         key: 'column4',
         name: 'ObservationList.WhatHappened',
         fieldName: 'whatHappened',
         minWidth: 50,
         maxWidth: i18next.language == 'gb' ? 200 : 160,
         isResizable: true,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => <CellContainer>{item.whatHappened}</CellContainer>,
         sortValueResolver: (item: NearMiss) => item.whatHappened
      },
      {
         key: 'column5',
         name: 'ObservationList.Location',
         fieldName: 'locationType',
         minWidth: 60,
         maxWidth: 100,
         isResizable: false,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => <CellContainer>{i18next.t(`ObservationList.${item.locationTypeDescription}`)}</CellContainer>
      },
      {
         key: 'column6',
         name: 'ObservationList.LocationDescription',
         fieldName: 'offSiteLocationDescription',
         minWidth: 140,
         maxWidth: 190,
         isResizable: true,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => <CellContainer>{item.offSiteLocation?.locationDescription}</CellContainer>
      },
      {
         key: 'column7',
         name: 'ObservationList.CustomerSite',
         fieldName: 'offSiteLocationCustomerSite',
         minWidth: 85,
         maxWidth: 100,
         isResizable: false,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => <CellContainer>{item.offSiteLocation?.customerSiteName}</CellContainer>
      },
      {
         key: 'column8',
         name: 'ObservationList.Depot',
         fieldName: 'depotPcName',
         minWidth: 80,
         maxWidth: 150,
         isResizable: true,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => <CellContainer>{item.depotPcName}</CellContainer>
      },
      {
         key: 'column9',
         name: 'ObservationList.Category',
         fieldName: 'observationCategoryName',
         minWidth: 80,
         maxWidth: 110,
         isResizable: false,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted A to Z',
         sortDescendingAriaLabel: 'Sorted Z to A',
         onColumnClick: handleColumnClick,
         data: 'string',
         isPadded: true,
         onRender: (item: NearMiss) => (
            <CellContainer>
               {i18next.language === 'de' ? item.observationCategoryNameDE : item.observationCategoryName}
            </CellContainer>
         )
      },
      {
         key: 'column10',
         name: 'ObservationList.Status',
         fieldName: 'observationStatus',
         minWidth: 60,
         maxWidth: 70,
         isResizable: false,
         isSorted: false,
         isSortedDescending: false,
         sortAscendingAriaLabel: 'Sorted Open first',
         sortDescendingAriaLabel: 'Sorted Open last',
         onColumnClick: handleColumnClick,
         data: 'date',
         isPadded: true,
         onRender: (item: NearMiss) => (
            <CellContainer>{i18next.t(`ObservationFilter.${item.observationStatus.toProperCase()}`)}</CellContainer>
         ),
         sortValueResolver: (item: NearMiss) => {
            i18next.t(`ObservationFilter.${item.observationStatus.toProperCase()}`);
         }
      },
      {
         key: 'column11',
         name: '',
         minWidth: 20,
         maxWidth: 20,
         isResizable: false,
         data: 'date',
         isPadded: true,
         onRender: (item: NearMiss) => (
            <LinkButton
               text={i18next.t('ObservationList.View')}
               onClick={e => {
                  e.preventDefault();
                  handleRowClick(item);
               }}
            />
         )
      }
   ];
};
